import { useEffect, useState } from 'react'
import useSWR from 'swr'
import type {
  ArchivePlanParams,
  ArchivePlanResponse,
  CompensationPlansResponse,
  ExpandedCompensationPlan,
  ExpandedCompensationPlansResponse,
} from 'types/comp-plan'
import { fetchWithToken, fetchWithTokenWithStatus, postWithTokenWithStatus, putWithTokenWithStatus } from './http'

export function usePlan(
  token: string,
  userId: string | undefined,
  params: Record<string, string> | null,
  shouldFetch: boolean = true
) {
  const [isLoading, setIsLoading] = useState(false)
  const shouldFetchData = shouldFetch && userId != null && token.trim() !== ''

  const { data, error, mutate } = useSWR(
    shouldFetchData ? [getUrl(userId, params), token, params] : null,
    ([url, token, params]) => fetchWithToken(url, token, null)
    // {
    //   suspense: false,
    //   revalidateIfStale: true,
    //   revalidateOnFocus: true,
    //   revalidateOnMount: true,
    // }
  )

  useEffect(() => {
    if (shouldFetchData) {
      setIsLoading(true)
      mutate().finally(() => {
        setIsLoading(false)
      })
    }
  }, [shouldFetchData, mutate])

  const handleRefetch = () => {
    setIsLoading(true)
    mutate().finally(() => {
      setIsLoading(false)
    })
  }

  const plan = data as ExpandedCompensationPlan | undefined
  const notFound = !plan || !plan.plan || (data && 'error' in data && data.error === 'CompensationPlan not found')

  return {
    compPlan: notFound ? undefined : plan,
    isLoading,
    isError: error || (data && 'error' in data), // Consider both SWR error and API error
    notFound,
    errorMessage: (error as Error)?.message || (data && 'error' in data ? data.error : undefined),
    handleRefetch,
  }
}

function getUrl(userId: string, params: Record<string, string> | null): string {
  if (params && params.planId && params.revision) {
    return `compensation/plans/user/${userId}/${params.planId}/${params.revision}`
  }
  return `compensation/plans/user/${userId}/latest`
}

export function usePlanWithStatus(
  token: string,
  userId: string | undefined,
  params: Record<string, string> | null,
  shouldFetch: boolean = true
) {
  const [isLoading, setIsLoading] = useState(false)
  const shouldFetchData = shouldFetch && userId != null && token.trim() !== ''

  const { data, error, mutate } = useSWR(
    shouldFetchData ? [getUrl(userId, params), token, params] : null,
    ([url, token, params]) => fetchWithTokenWithStatus(url, token, null),
    {
      shouldRetryOnError: false,
    }
  )

  useEffect(() => {
    if (shouldFetchData) {
      setIsLoading(true)
      mutate().finally(() => {
        setIsLoading(false)
      })
    }
  }, [shouldFetchData, mutate])

  const handleRefetch = () => {
    setIsLoading(true)
    mutate().finally(() => {
      setIsLoading(false)
    })
  }

  const { data: responseData, status } = data || { data: undefined, status: undefined }
  const plan = responseData as ExpandedCompensationPlan | undefined
  const notFound =
    !plan ||
    !plan.plan ||
    (responseData && 'error' in responseData && responseData.error === 'CompensationPlan not found')

  return {
    compPlan: notFound ? undefined : plan,
    isLoading,
    isError: error || (data && 'error' in data), // Consider both SWR error and API error
    notFound,
    errorMessage: (error as Error)?.message || (data && 'error' in data ? data.error : undefined),
    status: status || 0,
    handleRefetch,
  }
}

export function useArchivePlan(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const archivePlan = async (params: ArchivePlanParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, status } = await postWithTokenWithStatus('compensation/plans/archive', token, params)

      if (status >= 400) {
        throw new Error(data.error || 'Failed to archive plan')
      }

      return data as ArchivePlanResponse
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive plan')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    archivePlan,
    isLoading,
    error,
  }
}

export function useFetchPlans(token: string, userId: string, params: [[string, string]] | null) {
  const { data, error, isLoading } = useSWR(
    [`compensation/plans/user/${userId}`, token, params],
    ([url, token, params]) => fetchWithToken(url, token, params)
  )

  return {
    compPlans: data as CompensationPlansResponse,
    isLoading,
    isError: error,
  }
}

export function useFetchPlansWithAccruals(
  token: string,
  userId: string | undefined,
  params: [[string, string]] | null
) {
  const shouldFetchData = userId != null && userId.trim() !== '' && token.trim() !== ''

  const { data, error, isLoading } = useSWR(
    shouldFetchData ? [`dashboard/expanded-plans/${userId}`, token, params] : null,
    ([url, token, params]) => fetchWithToken(url, token, params)
  )

  return {
    compPlans: data as ExpandedCompensationPlansResponse,
    isLoading,
    isError: error,
  }
}

export function useFetchOrganizationPlans(token: string, orgId: string, params: [[string, string]] | null) {
  const { data, error, isLoading } = useSWR(
    [`compensation/plans/organization/${orgId}`, token, params],
    ([url, token, params]) => fetchWithToken(url, token, params)
  )

  return {
    plans: data?.plans ? (data?.plans as ExpandedCompensationPlan[]) : [],
    isLoading,
    isError: error,
  }
}

interface ClosePlanParams {
  planId: string
  endDate: string
}

interface ReopenPlanParams {
  planId: string
  rootOrganizationId: string
}

interface UpdatePlanOrganizationParams {
  planId: string
  planRevision: number
  organizationId: string
}

export function useClosePlan(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const closePlan = async (params: ClosePlanParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, status } = await postWithTokenWithStatus('compensation/plans/close', token, params)

      if (status >= 400) {
        throw new Error(data.error || 'Failed to close plan')
      }

      return data as ExpandedCompensationPlan
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close plan')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    closePlan,
    isLoading,
    error,
  }
}

export function useReopenPlan(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reopenPlan = async (params: ReopenPlanParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, status } = await postWithTokenWithStatus('compensation/plans/reopen', token, params)

      if (status >= 400) {
        throw new Error(data.error || 'Failed to reopen plan')
      }

      return data as ExpandedCompensationPlan
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reopen plan')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    reopenPlan,
    isLoading,
    error,
  }
}

interface ClearFinalizationParams {
  planId: string
  planRevision: number
}

export function useClearFinalization(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearFinalization = async (params: ClearFinalizationParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, status } = await postWithTokenWithStatus('compensation/plans/clear-finalization', token, params)

      if (status >= 400) {
        throw new Error(data.error || 'Failed to clear finalization')
      }

      return data as ExpandedCompensationPlan
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear finalization')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    clearFinalization,
    isLoading,
    error,
  }
}

export function useUpdatePlanOrganization(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updatePlanOrganization = async (params: UpdatePlanOrganizationParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, status } = await putWithTokenWithStatus(`compensation/plans/${params.planId}/organization`, token, {
        planRevision: params.planRevision,
        organizationId: params.organizationId,
      })

      if (status >= 400) {
        throw new Error(data.error || 'Failed to update plan organization')
      }

      return data as ExpandedCompensationPlan
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update plan organization')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    updatePlanOrganization,
    isLoading,
    error,
  }
}

interface BulkClosePlansParams {
  planIds: Array<{
    planId: string
    planRevision: number
  }>
  rootOrganizationId: string
  endDate: string
}

interface BulkClosePlansResult {
  planId: string
  success: boolean
  error?: string
  plan?: any
}

interface BulkClosePlansResponse {
  results: BulkClosePlansResult[]
  summary: {
    total: number
    successful: number
    failed: number
  }
}

export function useBulkClosePlans(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const bulkClosePlans = async (params: BulkClosePlansParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, status } = await postWithTokenWithStatus('compensation/plans/bulk-close', token, params)

      if (status >= 400) {
        throw new Error(data.error || 'Failed to close plans')
      }

      return data as BulkClosePlansResponse
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close plans')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    bulkClosePlans,
    isLoading,
    error,
  }
}

interface CreatePlanParams {
  plan: {
    userId: string
    templateId?: string
    templateRevision?: number
    organizationId: string
    rootOrganizationId: string
    rootPeriodId: string
    periodId: string
    periodProration: number
    position: string
    compensationModel: string
    baseSalary: number
    variablePercent: number
    variableCap: number
    variableAllocations: any[]
    effectiveDate: string
  }
  components: string[]
  targets: any[]
}

export function useCreatePlan(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createPlan = async (params: CreatePlanParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, status } = await postWithTokenWithStatus('compensation/plans', token, params)

      if (status >= 400) {
        throw new Error(data.error || 'Failed to create plan')
      }

      return data as ExpandedCompensationPlan
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create plan')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    createPlan,
    isLoading,
    error,
  }
}

export function usePlanRevision(
  token: string,
  targetUserId: string | undefined,
  planId: string | undefined,
  revision: number | undefined,
  shouldFetch: boolean = true
) {
  const [isLoading, setIsLoading] = useState(false)
  const shouldFetchData =
    shouldFetch && targetUserId != null && planId != null && revision != null && token.trim() !== ''

  const { data, error, mutate } = useSWR(
    shouldFetchData ? [`compensation/plans/user/${targetUserId}/${planId}/${revision}`, token] : null,
    ([url, token]) => fetchWithToken(url, token, null)
  )

  useEffect(() => {
    if (shouldFetchData) {
      setIsLoading(true)
      mutate().finally(() => {
        setIsLoading(false)
      })
    }
  }, [shouldFetchData, mutate])

  const handleRefetch = () => {
    setIsLoading(true)
    mutate().finally(() => {
      setIsLoading(false)
    })
  }

  const plan = data as ExpandedCompensationPlan | undefined
  const notFound = !plan || !plan.plan || (data && 'error' in data && data.error === 'CompensationPlan not found')

  return {
    compPlan: notFound ? undefined : plan,
    isLoading,
    isError: error || (data && 'error' in data),
    notFound,
    errorMessage: (error as Error)?.message || (data && 'error' in data ? data.error : undefined),
    handleRefetch,
  }
}

interface BulkHidePlansParams {
  planIds: Array<{
    planId: string
    planRevision: number
  }>
  rootOrganizationId: string
}

interface BulkShowPlansParams {
  planIds: Array<{
    planId: string
    planRevision: number
  }>
  rootOrganizationId: string
}

export function useHidePlan(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hidePlan = async (params: BulkHidePlansParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, status } = await postWithTokenWithStatus('compensation/plans/bulk-hide', token, params)

      if (status >= 400) {
        throw new Error(data.error || 'Failed to hide plans')
      }

      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to hide plans')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    hidePlan,
    isLoading,
    error,
  }
}

export function useShowPlan(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const showPlan = async (params: BulkShowPlansParams) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, status } = await postWithTokenWithStatus('compensation/plans/bulk-show', token, params)

      if (status >= 400) {
        throw new Error(data.error || 'Failed to show plans')
      }

      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to show plans')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    showPlan,
    isLoading,
    error,
  }
}
